import React, { useCallback, useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import { Auth } from 'aws-amplify';
import Logout from '../../pages/logout/Logout';
import i18nInstance from '../../../translations';
import { IStatus, IError } from '../../../types';

type AuthContextProps = {
  authUser: any;
  reloadAuthUser: any;
  setAuthUser: any;
};

export const AuthContext = React.createContext<Partial<AuthContextProps>>({});

export const setLanguagePromise = (language: string) => {
  if (language && !i18nInstance.language.startsWith(language)) {
    i18nInstance.changeLanguage(language).then();
  }
};

const AuthProvider = (props: { children: any }) => {
  const { children } = props;
  const [auth, setAuth] = useState<{ authUser: any; status: IStatus }>({
    authUser: null,
    status: { value: 'idle', code: null },
  });

  const { authUser, status } = auth;

  const reloadAuthUser = useCallback(() => {
    setAuth({
      authUser: null,
      status: { value: 'pending', code: null },
    });
    Auth.currentAuthenticatedUser()
      .then((data) => {
        setAuth({
          authUser: data,
          status: { value: 'resolved', code: 200 },
        });
      })
      .catch((err: IError) => {
        setAuth({
          authUser: null,
          status: { value: 'rejected', code: err.status },
        });
      });
  }, []);

  const setAuthUser = useCallback(
    (user) => setAuth((state) => ({ ...state, authUser: user })),
    [setAuth],
  );

  useEffect(() => {
    reloadAuthUser();
  }, [reloadAuthUser]);

  if (status.value === 'rejected') {
    return <Logout />;
  }

  if (status.value === 'idle' || status.value === 'pending') {
    return null;
  }

  return (
    <AuthContext.Provider value={{ authUser, reloadAuthUser, setAuthUser }}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node,
};

AuthProvider.defaultProps = {
  children: null,
};

export default AuthProvider;
